
































































import { Component, Vue } from "vue-property-decorator";
import PortalInput from "@/components/common/PortalInput.vue";
import PortalMoneyInput from "@/components/common/PortalMoneyInput.vue";
import PortalSelect from "@/components/common/PortalSelect.vue";
import SelectOption from "@/components/common/SelectOption";
import { BalanceEffect, TransactionType } from "@/constants/PaymentConstants";
import TaxObligationLedgerPayload from "@/dto/tax/TaxObligationLedgerPayload";
import { TaxObligationLedgerRecordDTO } from "@/dto/tax/TaxObligationDTO";
import { getTaxObligationLiabilityTypeLabel } from "@/constants/TaxObligationLiabilityType";
import TaxObligationService from "@/services/TaxObligationService";
import Application from "@/state/Application";
import { processError } from "@/utils/ComponentUtils";

@Component({
  components: {
    PortalInput,
    PortalMoneyInput,
    PortalSelect
  }
})
export default class AddLedgerRecordModal extends Vue {
  private taxObligationId: number | null = null;
  private ledger: TaxObligationLedgerRecordDTO[] = [];
  private amountCents: number | null = null;
  private form: Partial<TaxObligationLedgerPayload> = {
    balanceEffect: BalanceEffect.INCREASE,
    transactionType: TransactionType.PENALTY_ASSESSMENT,
    relatedRecordId: null
  };

  get balanceEffectOptions() {
    return Object.values(BalanceEffect).map(val => 
      SelectOption.builder().title(this.$stringUtils.enumToFormattedSentence(val)).value(val).build()
    );
  }

  get transactionTypeOptions() {
    const forbiddenTypes = [
      TransactionType.LIABILITY,
      TransactionType.OBLIGATION_PAYMENT,
      TransactionType.PENALTY_PAYMENT,
      TransactionType.TAX_BILL_ASSESSMENT
    ];
    return Object.values(TransactionType)
      .filter(val => !forbiddenTypes.includes(val))
      .map(val => 
        SelectOption.builder().title(this.$stringUtils.enumToFormattedSentence(val)).value(val).build()
      );
  }

  get availableObligations(): TaxObligationLedgerRecordDTO[] {
    return this.ledger.filter(r => r.transactionType === TransactionType.LIABILITY);
  }

  get relatedObligationOptions(): SelectOption[] {
    return this.availableObligations.map(r => {
      const typeLabel = r.liabilityType ? getTaxObligationLiabilityTypeLabel(r.liabilityType) : this.$stringUtils.enumToFormattedSentence(r.transactionType);
      const amountLabel = this.formatCurrency(r.amountCents / 100);
      return SelectOption.builder()
        .title(`${typeLabel} (${this.$dateUtils.formatDateToAmericanFormat(r.createDate)}) - ${amountLabel}`)
        .value(r.id)
        .build();
    });
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  }

  get warningMessage(): string | null {
    if (this.form.transactionType !== TransactionType.PENALTY_ASSESSMENT || !this.form.relatedRecordId || !this.amountCents) {
      return null;
    }

    const related = this.availableObligations.find(r => r.id === this.form.relatedRecordId);
    if (related && this.amountCents > related.amountCents) {
      return this.$t('tax_calendar.warnings.penalty_exceeds_liability') as string;
    }

    return null;
  }

  beforeOpen(event: any) {
    this.taxObligationId = event.params.taxObligationId;
    this.ledger = event.params.ledger || [];
    this.amountCents = null;
    this.form = {
      balanceEffect: BalanceEffect.INCREASE,
      transactionType: TransactionType.PENALTY_ASSESSMENT,
      relatedRecordId: null
    };
    this.$validator.reset();
  }

  closeModal() {
    this.$modal.hide("add-ledger-record-modal");
  }

  onTransactionTypeChange(val: TransactionType) {
    if (val !== TransactionType.PENALTY_ASSESSMENT) {
      this.form.relatedRecordId = null;
    }

    if (val === TransactionType.ADJUSTMENT) {
      return;
    }

    const increaseTypes = [TransactionType.LIABILITY, TransactionType.PENALTY_ASSESSMENT, TransactionType.INTEREST];
    const decreaseTypes = [TransactionType.CREDIT];

    if (increaseTypes.includes(val)) {
      this.form.balanceEffect = BalanceEffect.INCREASE;
    } else if (decreaseTypes.includes(val)) {
      this.form.balanceEffect = BalanceEffect.DECREASE;
    }
  }

  get isValid(): boolean {
    return !!(this.amountCents && this.form.balanceEffect && this.form.transactionType);
  }

  async submit() {
    const valid = await this.$validator.validateAll();
    if (!valid || !this.taxObligationId) return;

    const payload: TaxObligationLedgerPayload = {
      amountCents: this.amountCents!,
      balanceEffect: this.form.balanceEffect!,
      transactionType: this.form.transactionType!,
      relatedRecordId: this.form.relatedRecordId
    };

    Application.startLoading();
    TaxObligationService.addLedgerRecord(this.taxObligationId, payload).then(
      () => {
        Application.stopLoading();
        this.$emit("added");
        this.closeModal();
      },
      err => {
        Application.stopLoading();
        processError(err, this);
      }
    );
  }
}
